import { forwardRef, RefObject } from 'react';

import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Stack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { CheckOutline, CrossOutline, IndividualRole, useToast } from '@aslanpay/portal-common-lib';

import PhoneNumberInput from 'app/components/UI/Inputs/PhoneNumberInput';
import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';

import { Role } from 'app/constants/individual';

import useUpdateIndividual from 'app/hooks/useUpdateIndividual';

import { myProfileFormSchema } from 'app/utils/schemas';

import { MyProfileEditFormFields, MyProfileEditProps } from './types';

const MyProfileEdit = forwardRef<HTMLDivElement, MyProfileEditProps>(
  ({ admin, company, onCloseEditMode }, ref) => {
    const queryClient = useQueryClient();

    const toast = useToast();

    const {
      register,
      control,
      handleSubmit,
      watch,
      reset,
      setFocus,
      setValue,
      formState: { errors, isValid, isDirty },
    } = useForm<MyProfileEditFormFields>({
      mode: 'onBlur',
      defaultValues: admin as MyProfileEditFormFields,
      resolver: yupResolver(myProfileFormSchema),
    });

    const watchFirstName = watch('firstName');
    const watchLastName = watch('lastName');
    const watchEmail = watch('email');

    const { mutate: updateAdmin, isLoading: isUpdatingAdmin } = useUpdateIndividual({
      onSuccess: () => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'Profile details saved',
          description: 'The user details were successfully saved!',
        });

        queryClient.invalidateQueries({ queryKey: ['fetchIndividual', admin?.id] });

        onCloseEditMode();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'It was not possible to save the user details.',
        });
      },
    });

    const handleCancelUpdate = () => {
      reset({
        firstName: admin?.firstName,
        lastName: admin?.lastName,
        email: admin?.email,
        phoneNumber: admin?.phoneNumber,
      });

      onCloseEditMode();
    };

    const handleUpdateAdmin = (data: MyProfileEditFormFields) => {
      updateAdmin({
        id: admin?.id || '',
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        role: 'ASLAN_ADMIN',
      });
    };

    const resetAndFocusField = (field: keyof MyProfileEditFormFields) => {
      setValue(field, '', { shouldValidate: true, shouldDirty: true });
      setFocus(field);
    };

    return (
      <Flex mt="20px" gap="60px" alignItems="flex-start">
        <Flex flexDirection="column" gap="40px" flexGrow={1} maxW="492px">
          <ProfileSection title="My IDs">
            <ProfileField label="Aslan ID" value={admin?.friendlyId} />
          </ProfileSection>
          {company && (
            <ProfileSection title="User level">
              <ProfileField label="My role" value={Role[admin?.role as IndividualRole]} />
              <ProfileField label="Company name" value={company?.name} />
            </ProfileSection>
          )}
        </Flex>
        <ProfileSection title="General details" isEditMode>
          <form id="edit-my-profile" onSubmit={handleSubmit(handleUpdateAdmin)} noValidate>
            <Stack spacing="20px">
              <HStack spacing="20px" alignItems="flex-start">
                <FormControl isInvalid={!!errors?.firstName?.message} isRequired>
                  <FormLabel>First name</FormLabel>
                  <InputGroup>
                    <Input type="text" placeholder="Enter first name" {...register('firstName')} />
                    {watchFirstName && (
                      <InputRightElement
                        h="full"
                        cursor="pointer"
                        onClick={() => resetAndFocusField('firstName')}
                      >
                        <CrossOutline color="brand.darkGray" boxSize="24px" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <FormErrorMessage>{errors?.firstName?.message}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors?.lastName?.message} isRequired>
                  <FormLabel>Last name</FormLabel>
                  <InputGroup>
                    <Input type="text" placeholder="Enter last name" {...register('lastName')} />
                    {watchLastName && (
                      <InputRightElement
                        h="full"
                        cursor="pointer"
                        onClick={() => resetAndFocusField('lastName')}
                      >
                        <CrossOutline color="brand.darkGray" boxSize="24px" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
                </FormControl>
              </HStack>
              <FormControl isInvalid={!!errors?.email?.message} isRequired>
                <FormLabel>Email</FormLabel>
                <InputGroup>
                  <Input type="email" placeholder="Enter your email" {...register('email')} />
                  {watchEmail && (
                    <InputRightElement
                      h="full"
                      cursor="pointer"
                      onClick={() => resetAndFocusField('email')}
                    >
                      <CrossOutline color="brand.darkGray" boxSize="24px" />
                    </InputRightElement>
                  )}
                </InputGroup>
                <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors?.phoneNumber?.message} isRequired>
                <FormLabel>Phone number</FormLabel>
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <PhoneNumberInput defaultValue={value} onChange={onChange} onBlur={onBlur} />
                  )}
                />
                <FormErrorMessage>{errors?.phoneNumber?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
            <Portal containerRef={ref as RefObject<HTMLElement>}>
              <Flex gap="20px" alignItems="flex-start">
                <Button variant="outline" size="sm" onClick={handleCancelUpdate}>
                  Cancel
                </Button>
                <Button
                  leftIcon={<CheckOutline boxSize="20px" />}
                  size="sm"
                  type="submit"
                  form="edit-my-profile"
                  isLoading={isUpdatingAdmin}
                  isDisabled={!isValid || !isDirty}
                >
                  Save changes
                </Button>
              </Flex>
            </Portal>
          </form>
        </ProfileSection>
      </Flex>
    );
  }
);

MyProfileEdit.displayName = 'MyProfileEdit';

export default MyProfileEdit;
